import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  RefreshCw, Loader2, TrendingUp, TrendingDown,
  Search, Download, Eye, CheckCircle, Clock, XCircle,
  RotateCcw, Filter, Calendar, BarChart3, Wallet, ArrowUpRight
} from 'lucide-react';

const PAYMENT_STATUS = {
  paid: { label: 'Pago', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  refunded: { label: 'Estornado', color: 'bg-red-100 text-red-700', icon: RotateCcw },
  failed: { label: 'Falhou', color: 'bg-gray-100 text-gray-700', icon: XCircle }
};

// Backend may return "completed" (legacy) or "paid" — treat both the same in the UI
const normalizeStatus = (status) => (status === 'completed' ? 'paid' : status);

export default function AdminFinancial() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [dashboard, setDashboard] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
      return;
    }
    fetchAll();
  }, [user, navigate]);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchDashboard(), fetchPayments()]);
    setLoading(false);
  };

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/financial/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchPayments = async (statusOverride) => {
    setPaymentsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      const effectiveStatus = statusOverride !== undefined ? statusOverride : filters.status;
      if (effectiveStatus) params.append('status', effectiveStatus);

      const res = await axios.get(`${API}/admin/financial/payments?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(res.data.payments || []);
    } catch (error) {
      console.error(error);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handleMarkPaid = async (paymentId) => {
    setActionLoading(paymentId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/financial/payments/${paymentId}/mark-paid`, 
        { reason: 'Confirmado manualmente pelo admin' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Pagamento confirmado!');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao confirmar pagamento');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRefund = async (paymentId) => {
    if (!window.confirm('Tem certeza que deseja estornar este pagamento?')) return;
    
    setActionLoading(paymentId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/financial/payments/${paymentId}/refund`, 
        { reason: 'Estorno solicitado pelo admin' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Pagamento estornado!');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao estornar pagamento');
    } finally {
      setActionLoading(null);
    }
  };

  const exportCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/financial/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pagamentos_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Relatório exportado!');
    } catch (error) {
      toast.error('Erro ao exportar relatório');
    }
  };

  const viewPaymentDetails = async (paymentId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/financial/payments/${paymentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedPayment(res.data.payment);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const filteredPayments = payments.filter(p => {
    if (!filters.search) return true;
    const search = filters.search.toLowerCase();
    return (
      p.client?.name?.toLowerCase().includes(search) ||
      p.client?.email?.toLowerCase().includes(search) ||
      p.id?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <AdminLayout title="Visão Financeira" subtitle="Carregando...">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  const revenue = dashboard?.revenue || {};
  const transactions = dashboard?.transactions || {};
  const metrics = dashboard?.metrics || {};

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Visão Financeira</h1>
            <p className="text-slate-500">Receitas e Pagamentos</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportCSV}>
              <Download className="w-4 h-4 mr-2" /> Exportar
            </Button>
            <Button variant="outline" size="sm" onClick={fetchAll}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Revenue Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700">Receita Total</p>
                <p className="text-2xl font-bold text-green-800">
                  R$ {revenue.total?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}
                </p>
              </div>
              <div className="bg-green-200 p-3 rounded-full">
                <Wallet className="w-6 h-6 text-green-700" />
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Este Mês</p>
                <p className="text-2xl font-bold">
                  R$ {revenue.monthly?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}
                </p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            {revenue.growth_percent !== undefined && (
              <div className={`mt-2 flex items-center text-sm ${revenue.growth_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {revenue.growth_percent >= 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
                {revenue.growth_percent >= 0 ? '+' : ''}{revenue.growth_percent}% vs mês anterior
              </div>
            )}
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Ticket Médio</p>
                <p className="text-2xl font-bold">
                  R$ {metrics.avg_ticket?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}
                </p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <BarChart3 className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Taxa de Conversão</p>
                <p className="text-2xl font-bold">{metrics.conversion_rate || 0}%</p>
              </div>
              <div className="bg-orange-100 p-3 rounded-full">
                <ArrowUpRight className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Transaction Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(PAYMENT_STATUS).map(([key, config]) => {
            const Icon = config.icon;
            const count = transactions[key] || 0;
            return (
              <Card 
                key={key}
                data-testid={`financial-status-card-${key}`}
                className={`p-4 cursor-pointer transition-all ${filters.status === key ? 'ring-2 ring-primary' : ''}`}
                onClick={() => {
                  const newStatus = filters.status === key ? '' : key;
                  setFilters({ ...filters, status: newStatus });
                  fetchPayments(newStatus);
                }}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${config.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xl font-bold">{count}</p>
                    <p className="text-xs text-muted-foreground">{config.label}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Payment Detail Modal */}
        {selectedPayment && (
          <Card className="p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold">Detalhes do Pagamento</h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedPayment(null)}>
                <XCircle className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-muted-foreground">ID da Transação</p>
                <p className="font-mono text-sm">{selectedPayment.transaction_id || selectedPayment.id}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Valor</p>
                <p className="text-xl font-bold text-green-600">R$ {selectedPayment.amount?.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Método</p>
                <p className="capitalize">{selectedPayment.payment_method || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Cliente</p>
                <p>{selectedPayment.client?.name || 'N/A'}</p>
                <p className="text-sm text-muted-foreground">{selectedPayment.client?.email}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Data</p>
                <p>{selectedPayment.created_at ? new Date(selectedPayment.created_at).toLocaleString('pt-BR') : 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <Badge className={PAYMENT_STATUS[normalizeStatus(selectedPayment.status)]?.color}>
                  {PAYMENT_STATUS[normalizeStatus(selectedPayment.status)]?.label}
                </Badge>
              </div>
            </div>
          </Card>
        )}

        {/* Filters */}
        <div className="flex gap-4 items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              placeholder="Buscar por cliente ou ID..."
              className="pl-10"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>
          {filters.status && (
            <Button variant="outline" size="sm" onClick={() => { setFilters({ ...filters, status: '' }); fetchPayments(''); }}>
              <Filter className="w-4 h-4 mr-2" /> Limpar filtro
            </Button>
          )}
        </div>

        {/* Payments List */}
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Cliente</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Valor</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Método</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Data</th>
                  <th className="text-right p-4 text-sm font-medium text-muted-foreground">Ações</th>
                </tr>
              </thead>
              <tbody>
                {paymentsLoading ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : filteredPayments.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-muted-foreground">
                      Nenhum pagamento encontrado
                    </td>
                  </tr>
                ) : (
                  filteredPayments.map(payment => {
                    const normalized = normalizeStatus(payment.status);
                    const statusConfig = PAYMENT_STATUS[normalized] || PAYMENT_STATUS.pending;
                    return (
                      <tr key={payment.id} className="border-b hover:bg-slate-50">
                        <td className="p-4">
                          <p className="font-medium">{payment.client?.name || 'N/A'}</p>
                          <p className="text-sm text-muted-foreground">{payment.client?.email}</p>
                        </td>
                        <td className="p-4">
                          <span className="font-semibold">R$ {payment.amount?.toFixed(2)}</span>
                        </td>
                        <td className="p-4 capitalize">
                          {payment.payment_method || 'N/A'}
                        </td>
                        <td className="p-4">
                          <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {payment.created_at ? new Date(payment.created_at).toLocaleDateString('pt-BR') : 'N/A'}
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => viewPaymentDetails(payment.id)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            {normalized === 'pending' && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-green-600"
                                onClick={() => handleMarkPaid(payment.id)}
                                disabled={actionLoading === payment.id}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                            {normalized === 'paid' && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-red-600"
                                onClick={() => handleRefund(payment.id)}
                                disabled={actionLoading === payment.id}
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            )}
                            {actionLoading === payment.id && (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}
